import { useCreateOrUpdateMultipleSampleInBibliography } from "@/api/related-fields";
import { LinkableType } from "@/components/LinksManagement";
import {
  applyFieldConfigOverrides,
  FormLayoutElement,
} from "@/config/forms/general";
import { MultipleSampleInBibliography } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";

const MultiplesampleInBibliographyDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
}: {
  fields: FormLayoutElement[] | undefined;
  data: MultipleSampleInBibliography;
  queryId: string;
  type: LinkableType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const schema = z.object({
    multiplesampleDansBibliographieDescriptionReproduitPage: z
      .string()
      .optional(),
    multiplesampleDansBibliographieDescriptionReproduitPlanche: z
      .string()
      .optional(),
    multiplesampleDansBibliographieDescriptionPlancheNonNumerotee: z
      .boolean()
      .optional(),
    multiplesampleDansBibliographieDescriptionCitePage: z.string().optional(),
    multiplesampleDansBibliographieDescriptionListePage: z.string().optional(),
    multiplesampleDansBibliographieDescriptionSousNumero: z.string().optional(),
    multiplesampleDansBibliographieDescriptionPhotographe: z
      .string()
      .optional(),
    multiplesampleDansBibliographieDescriptionReproductionCouleur: z
      .boolean()
      .optional(),
    multiSampleInBiblioDescReproCover: z
      .boolean()
      .optional(),
    multiplesampleDansBibliographieDescriptionNonPagine: z.boolean().optional(),
    multiplesampleDansBibliographieDescriptionTexteLibre: z.string().optional(),
    multiplesampleDansBibliographieNotes: z.string().optional(),
    bibliography: z.object({
      bibliographieDescriptionNumeroBibliographie: z.string().optional(),
      bibliographieDescriptionResumeLong: z.string().optional(),
    }),
  });

  const { mutate: createOrUpdateMultipleSampleInBibliography } =
    useCreateOrUpdateMultipleSampleInBibliography(queryId, type);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      bibliography: data.bibliography,
      multiplesampleDansBibliographieDescriptionReproduitPage:
        data.multiplesampleDansBibliographieDescriptionReproduitPage,
      multiplesampleDansBibliographieDescriptionReproduitPlanche:
        data.multiplesampleDansBibliographieDescriptionReproduitPlanche,
      multiplesampleDansBibliographieDescriptionPlancheNonNumerotee:
        data.multiplesampleDansBibliographieDescriptionPlancheNonNumerotee,
      multiplesampleDansBibliographieDescriptionCitePage:
        data.multiplesampleDansBibliographieDescriptionCitePage,
      multiplesampleDansBibliographieDescriptionListePage:
        data.multiplesampleDansBibliographieDescriptionListePage,
      multiplesampleDansBibliographieDescriptionSousNumero:
        data.multiplesampleDansBibliographieDescriptionSousNumero,
      multiplesampleDansBibliographieDescriptionPhotographe:
        data.multiplesampleDansBibliographieDescriptionPhotographe,
      multiplesampleDansBibliographieDescriptionReproductionCouleur:
        data.multiplesampleDansBibliographieDescriptionReproductionCouleur,
      multiSampleInBiblioDescReproCover: data.multiSampleInBiblioDescReproCover,
      multiplesampleDansBibliographieDescriptionNonPagine:
        data.multiplesampleDansBibliographieDescriptionNonPagine,
      multiplesampleDansBibliographieDescriptionTexteLibre:
        data.multiplesampleDansBibliographieDescriptionTexteLibre,
      multiplesampleDansBibliographieNotes:
        data.multiplesampleDansBibliographieNotes,
    },
  });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    createOrUpdateMultipleSampleInBibliography({
      id: data.id ?? "",
      multiplesampleId: data.multiplesample.id,
      bibliographyId: data.bibliography.id,
      multiplesampleDansBibliographieDescriptionReproduitPage:
        formData.multiplesampleDansBibliographieDescriptionReproduitPage,
      multiplesampleDansBibliographieDescriptionCitePage:
        formData.multiplesampleDansBibliographieDescriptionCitePage,
      multiplesampleDansBibliographieDescriptionReproduitPlanche:
        formData.multiplesampleDansBibliographieDescriptionReproduitPlanche,
      multiplesampleDansBibliographieDescriptionPlancheNonNumerotee:
        formData.multiplesampleDansBibliographieDescriptionPlancheNonNumerotee,
      multiplesampleDansBibliographieDescriptionListePage:
        formData.multiplesampleDansBibliographieDescriptionListePage,
      multiplesampleDansBibliographieDescriptionSousNumero:
        formData.multiplesampleDansBibliographieDescriptionSousNumero,
      multiplesampleDansBibliographieDescriptionPhotographe:
        formData.multiplesampleDansBibliographieDescriptionPhotographe,
      multiplesampleDansBibliographieDescriptionReproductionCouleur:
        formData.multiplesampleDansBibliographieDescriptionReproductionCouleur,
      multiSampleInBiblioDescReproCover:
        formData.multiSampleInBiblioDescReproCover,
      multiplesampleDansBibliographieDescriptionNonPagine:
        formData.multiplesampleDansBibliographieDescriptionNonPagine,
      multiplesampleDansBibliographieDescriptionTexteLibre:
        formData.multiplesampleDansBibliographieDescriptionTexteLibre,
      multiplesampleDansBibliographieNotes:
        formData.multiplesampleDansBibliographieNotes,
    });
  };
  const enhancedFields = applyFieldConfigOverrides(fields ?? [], {
    multiplesampleDansBibliographieDescriptionTexteLibre: {
      expandOnOverflow: true,
    },
  });

  return (
    <LinksDrawerLayout
      form={form}
      fields={enhancedFields}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={{
        attachments: data.multiplesample.attachments ?? [],
        type: "multiplesample",
        alt:
          data.multiplesample
            .exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
      }}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
};

export default MultiplesampleInBibliographyDrawer;
